import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { Checkbox } from '@/components/ui/checkbox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface SupplierProductFormData {
  productId: string;
  supplierCode: string;
  unitCost: number;
  currency: string;
  minOrderQty: number;
  leadTimeDays: number;
  isPreferred: boolean;
  notes: string;
}

interface SupplierProductModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplierId: string;
  supplierName: string;
  existingProductIds?: string[];
  editingProduct?: {
    id: string;
    productId: string;
    supplierCode: string | null;
    unitCost: number;
    currency: string;
    minOrderQty: number;
    leadTimeDays: number;
    isPreferred: boolean;
    notes?: string | null;
  };
}

export function SupplierProductModal({
  open,
  onOpenChange,
  supplierId,
  supplierName,
  existingProductIds = [],
  editingProduct,
}: SupplierProductModalProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    register,
  } = useForm<SupplierProductFormData>({
    defaultValues: editingProduct
      ? {
          productId: editingProduct.productId,
          supplierCode: editingProduct.supplierCode || '',
          unitCost: editingProduct.unitCost,
          currency: editingProduct.currency,
          minOrderQty: editingProduct.minOrderQty,
          leadTimeDays: editingProduct.leadTimeDays,
          isPreferred: editingProduct.isPreferred,
          notes: editingProduct.notes || '',
        }
      : {
          productId: '',
          supplierCode: '',
          unitCost: 0,
          currency: 'EUR',
          minOrderQty: 1,
          leadTimeDays: 0,
          isPreferred: false,
          notes: '',
        },
  });

  const utils = trpc.useUtils();

  const { data: productsData } = trpc.tradingProduct.listForSelect.useQuery();

  const availableProducts = editingProduct
    ? productsData
    : productsData?.filter((p) => !existingProductIds.includes(p.id));

  const productOptions = (availableProducts || []).map((p) => ({
    value: p.id,
    label: `${p.fullCode} - ${p.name}`,
  }));

  const createMutation = trpc.tradingSupplierProduct.create.useMutation({
    onSuccess: () => {
      utils.tradingSupplierProduct.bySupplier.invalidate(supplierId);
      utils.tradingSupplier.list.invalidate();
      showSuccessToast(tCommon('created'));
      onOpenChange(false);
      reset();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const updateMutation = trpc.tradingSupplierProduct.update.useMutation({
    onSuccess: () => {
      utils.tradingSupplierProduct.bySupplier.invalidate(supplierId);
      showSuccessToast(tCommon('updated'));
      onOpenChange(false);
      reset();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const onSubmit = (data: SupplierProductFormData) => {
    if (editingProduct) {
      updateMutation.mutate({
        id: editingProduct.id,
        data: {
          supplierCode: data.supplierCode || null,
          unitCost: data.unitCost,
          currency: data.currency,
          minOrderQty: data.minOrderQty,
          leadTimeDays: data.leadTimeDays,
          isPreferred: data.isPreferred,
          notes: data.notes || null,
        },
      });
    } else {
      createMutation.mutate({
        supplierId,
        productId: data.productId,
        supplierCode: data.supplierCode || undefined,
        unitCost: data.unitCost,
        currency: data.currency,
        minOrderQty: data.minOrderQty,
        leadTimeDays: data.leadTimeDays,
        isPreferred: data.isPreferred,
        notes: data.notes || undefined,
      });
    }
  };

  const selectedProductId = watch('productId');
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editingProduct
              ? t('purchasing.supplierProducts.editProduct')
              : t('purchasing.supplierProducts.addProduct')}
          </DialogTitle>
          <DialogDescription>{supplierName}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {!editingProduct && (
            <div className="space-y-2">
              <Label>{t('purchasing.supplierProducts.product')}</Label>
              <Combobox
                options={productOptions}
                value={selectedProductId}
                onValueChange={(value) => setValue('productId', value)}
                placeholder={t('purchasing.supplierProducts.product')}
                searchPlaceholder={tCommon('search')}
                emptyText={t('purchasing.supplierProducts.noProducts')}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="supplierCode">{t('purchasing.supplierProducts.supplierCode')}</Label>
            <Input id="supplierCode" {...register('supplierCode')} placeholder="e.g. SUP-001" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="unitCost">{t('purchasing.supplierProducts.unitCost')}</Label>
              <Input
                id="unitCost"
                type="number"
                step="0.01"
                min="0"
                {...register('unitCost', { valueAsNumber: true })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">{t('purchasing.supplierProducts.currency')}</Label>
              <Input id="currency" {...register('currency')} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="minOrderQty">{t('purchasing.supplierProducts.minOrderQty')}</Label>
              <Input
                id="minOrderQty"
                type="number"
                min="1"
                {...register('minOrderQty', { valueAsNumber: true })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="leadTimeDays">{t('purchasing.supplierProducts.leadTimeDays')}</Label>
              <Input
                id="leadTimeDays"
                type="number"
                min="0"
                {...register('leadTimeDays', { valueAsNumber: true })}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="isPreferred"
              checked={watch('isPreferred')}
              onCheckedChange={(checked) => setValue('isPreferred', !!checked)}
            />
            <Label htmlFor="isPreferred" className="cursor-pointer">
              {t('purchasing.supplierProducts.isPreferred')}
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">{t('purchasing.supplierProducts.notes')}</Label>
            <Textarea id="notes" {...register('notes')} rows={2} />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || (!editingProduct && !selectedProductId)}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingProduct ? tCommon('save') : tCommon('create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
